/**
 * Tenant context in the nav bar (RBAC plan §3.3/§3.6).
 *
 * The plan's rule — a picker only above one company, a static label for one, nothing for none —
 * now lives in the shared library's `CompanyPicker`; this component supplies the names (the
 * token carries ids only) and the app's markup through `render`, so the nav bar keeps its own
 * dropdown (DEV-5751). Switching the active company is a client-side context change —
 * cloud-core stays the authority on which company's data a request may read (DEV-4482).
 */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CompanyPicker as LibraryCompanyPicker,
  useCompanyOptions,
  type CompanyPickerRenderProps,
} from "@cyngn/auth-react";

import { fetchSessionCompanies, getCompanyNameById } from "Slices/companies";
import { getCurrentUserCompanyName } from "Slices/currentUser";
import { useOnClickOutside } from "Utils/hooks";

import {
  CompanyPickerStyled,
  CompanyLabelStyled,
  CompanyToggleStyled,
  CompanyListStyled,
  CompanyListItemStyled,
} from "./styles";

export const CompanyPicker = memo(() => {
  const dispatch = useDispatch();
  const companyNameById = useSelector(getCompanyNameById);
  const legacyCompanyName = useSelector(getCurrentUserCompanyName);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const ref = useRef() as React.MutableRefObject<HTMLDivElement>;
  const handleClickOutside = useCallback(() => setShowDropdown(false), []);
  useOnClickOutside(ref, handleClickOutside);

  // The claim carries UUIDs only, so an RBAC session loads the names of exactly the companies
  // it is scoped to (DEV-5516) instead of waiting for a page that happens to list companies.
  // The thunk is a no-op once the store holds them.
  const companyCount = useCompanyOptions().length;
  useEffect(() => {
    if (companyCount === 0) return;
    dispatch(fetchSessionCompanies());
  }, [dispatch, companyCount]);

  const renderPicker = useCallback(
    ({
      companies,
      activeCompanyId,
      setActiveCompanyId,
      showPicker,
    }: CompanyPickerRenderProps) => {
      // A name is still only best effort: the active company falls back to the legacy
      // `User.company` name that cloud-core returns in dual mode.
      const nameOf = (companyId: string, name: string) =>
        companyNameById[companyId] ||
        (companyId === activeCompanyId ? legacyCompanyName : "") ||
        name;

      // Legacy sessions carry no company claim: keep showing the legacy label.
      if (companies.length === 0) {
        return legacyCompanyName ? (
          <CompanyPickerStyled>
            <CompanyLabelStyled data-test-id="company_label">
              {legacyCompanyName}
            </CompanyLabelStyled>
          </CompanyPickerStyled>
        ) : null;
      }

      if (!showPicker) {
        const only = companies[0];
        return (
          <CompanyPickerStyled>
            <CompanyLabelStyled data-test-id="company_label">
              {nameOf(only.id, only.name)}
            </CompanyLabelStyled>
          </CompanyPickerStyled>
        );
      }

      const activeCompany = companies.find(
        (company) => company.id === activeCompanyId
      );

      return (
        <CompanyPickerStyled ref={ref}>
          <CompanyToggleStyled
            data-test-id="company_picker"
            $active={showDropdown}
            onClick={() => setShowDropdown(!showDropdown)}
          >
            {activeCompany
              ? nameOf(activeCompany.id, activeCompany.name)
              : "Select company"}
          </CompanyToggleStyled>
          {showDropdown && (
            <CompanyListStyled data-test-id="company_picker_list">
              {companies.map((company) => (
                <CompanyListItemStyled
                  data-test-id={`company_option_${company.id}`}
                  key={company.id}
                  $active={company.id === activeCompanyId}
                  onClick={() => {
                    setActiveCompanyId(company.id);
                    setShowDropdown(false);
                  }}
                >
                  {nameOf(company.id, company.name)}
                </CompanyListItemStyled>
              ))}
            </CompanyListStyled>
          )}
        </CompanyPickerStyled>
      );
    },
    [companyNameById, legacyCompanyName, showDropdown]
  );

  return (
    <LibraryCompanyPicker
      companyNames={companyNameById}
      render={renderPicker}
    />
  );
});
